import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useConfig } from '../context/ConfigContext';
import { db } from '../firebase';
import {
  parseTimeSlotStartHour,
  sortTimeSlots,
  formatTimeSlotRange,
  isHourlyEquipment,
  describeEquipmentPrice,
  getEquipmentLineTotal,
} from '../utils/bookingUtils';
import { getBookingHourRange, fetchEquipmentAvailability } from '../utils/equipmentAvailabilityUtil';
import '../css/EquipmentRentalModal.css';
import { formatMoney } from '../utils/currencyUtil';

// "Mon, Aug 24" for an hour-picker date heading, parsed from "yyyy-MM-dd" as a
// LOCAL date — new Date('2026-08-24') is UTC midnight, which renders as the
// previous day for anyone behind UTC.
const formatPickerDate = (dateStr) => {
  const [y, m, d] = (dateStr || '').split('-').map(Number);
  if (!y || !m || !d) return dateStr || '';
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

/**
 * "Need equipments?" rental step shown after the guest taps Continue on the
 * details form (see UserInfoForm.jsx) and before the booking is actually
 * submitted. Guests check off any equipment they want to rent; picking one
 * reveals its quantity + subtotal inline right below it (no second modal),
 * and a running grand total sits above the Continue button. That total is
 * added to the booking's payment amount by the caller (see App.jsx's
 * createPendingBooking).
 *
 * Quantity choices are capped to what's actually still free for the guest's
 * date + time window (see equipmentAvailabilityUtil.js) — this is a live
 * read for UX purposes; the actual claim (and final say on availability)
 * happens atomically when the booking is created, so a last-second race
 * against another guest still can't over-book the equipment.
 *
 * The same equipment selection applies to EVERY date the guest has picked
 * (see App.jsx's createPendingBooking, which claims it separately per
 * date), so availability is checked across all of them and capped to the
 * bottleneck date — the guest can never pick more than what's free on
 * whichever selected date has the least left.
 *
 * Items configured as "Per hour" (Config -> Equipment -> Rental type)
 * additionally get an hour picker: every hour the guest already booked,
 * grouped by date, ticked by default. Unticking one drops both its charge and
 * its hold. Availability above is still read across each date's FULL session
 * span rather than per picked hour — that can under-report what's free for a
 * narrow pick, which errs toward refusing a rental rather than over-renting
 * one, and the atomic claim at booking time uses the real picked hours.
 *
 * @param {boolean} isOpen
 * @param {() => void} onClose - Cancel, back to the details form.
 * @param {(selection: {items: Array<{id: string, label: string, unit: string, price: number, qty: number, subtotal: number}>, total: number}) => void} onConfirm
 * @param {Object} selectedDates - { [dateStr]: { [timeSlot]: court[] } }, used to derive each date's rental time window.
 * @param {string} [sportId] - When set (see App.jsx once the booking flow is
 *   sport-aware — Config -> Sports & Courts), only that sport's equipment
 *   (Config -> Equipment) is offered. Left unset, every configured sport's
 *   equipment is shown together, which is exactly equivalent for a
 *   single-sport club.
 */
function EquipmentRentalModal({ isOpen, onClose, onConfirm, selectedDates, sportId = null }) {
  const { config } = useConfig();
  // Mirrors UserInfoForm's filter: this sport's items, minus anything the
  // club owns none of (inventoryTotal 0) — those can never be rented, so
  // listing them just to show "Fully booked for this time slot" is noise.
  const equipmentList = (config?.equipment || []).filter(item => (
    (!sportId || item.sportId === sportId) && (Number(item.inventoryTotal) || 0) > 0
  ));
  // { [equipmentId]: qty } — an id only appears here once checked; qty
  // defaults to 1 the moment it's checked.
  const [quantities, setQuantities] = useState({});
  // Hourly items only (Config -> Equipment -> Rental type): which of the
  // guest's own booked hours they want to keep each item for, as
  // { [equipmentId]: { [dateStr]: string[] } }. A session item never appears
  // here — it's one charge for the whole reservation regardless of hours.
  const [rentalHours, setRentalHours] = useState({});
  // { [equipmentId]: unitsRemaining } — the smallest "still free" figure
  // across every selected date's own time window (see the module doc
  // comment above).
  const [availability, setAvailability] = useState(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  // The open quantity picker, if any: { itemId, top, left, width, maxQty, unit }.
  // Rendered as its own position:fixed popup (see qtyMenu render below)
  // rather than a native <select>, so the option list can scroll reliably
  // instead of getting cut off at the viewport edge or clipped by erm-card's
  // own overflow-y:auto.
  const [qtyMenu, setQtyMenu] = useState(null);
  const cardRef = useRef(null);

  // Fetch fresh availability every time the modal opens (or the guest's
  // date/time selection changes), and reset any previous selection — stale
  // numbers from an earlier open shouldn't linger.
  useEffect(() => {
    if (!isOpen) return;
    setQuantities({});
    setRentalHours({});
    setQtyMenu(null);
    const dateEntries = Object.entries(selectedDates || {});
    if (dateEntries.length === 0) { setAvailability(null); return; }
    let cancelled = false;
    setLoadingAvailability(true);
    Promise.all(dateEntries.map(([dateStr, slots]) => {
      const { startHour, endHour } = getBookingHourRange(slots, parseTimeSlotStartHour);
      return fetchEquipmentAvailability(db, dateStr, startHour, endHour, config);
    }))
      .then(results => {
        if (cancelled) return;
        const combined = {};
        equipmentList.forEach(({ id }) => {
          combined[id] = Math.min(...results.map(r => (r?.[id] ?? Infinity)));
        });
        setAvailability(combined);
      })
      .catch(() => { if (!cancelled) setAvailability(null); })
      .finally(() => { if (!cancelled) setLoadingAvailability(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, JSON.stringify(selectedDates), sportId]);

  // Close the quantity popup on an outside click, or on scroll/resize —
  // its position is computed once from the trigger button's rect, so it'd
  // otherwise drift out of place if the card scrolls or the viewport
  // changes underneath it.
  useEffect(() => {
    if (!qtyMenu) return undefined;
    const handlePointerDown = (e) => {
      if (e.target.closest('.erm-qty-select') || e.target.closest('.erm-qty-menu')) return;
      setQtyMenu(null);
    };
    const handleDismiss = () => setQtyMenu(null);
    document.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('resize', handleDismiss);
    const cardEl = cardRef.current;
    cardEl?.addEventListener('scroll', handleDismiss);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('resize', handleDismiss);
      cardEl?.removeEventListener('scroll', handleDismiss);
    };
  }, [qtyMenu]);

  if (!isOpen) return null;

  const priceFor = (item) => Number(item.price) || 0;
  // Admin-configured total (Config -> Equipment). Used both as the "still
  // loading" placeholder and as a safety fallback if the live availability
  // fetch fails, so a config change is always reflected here even when the
  // time-based read can't complete.
  const totalFor = (item) => Number(item.inventoryTotal) || 0;
  const remainingFor = (item) => availability?.[item.id] ?? totalFor(item);

  // The guest's booked hours, grouped by date and in chronological order —
  // the menu an hourly item's hour picker is built from.
  const bookedHoursByDate = Object.entries(selectedDates || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dateStr, slots]) => ({ dateStr, timeSlots: sortTimeSlots(Object.keys(slots || {})) }))
    .filter(entry => entry.timeSlots.length > 0);

  /** Every booked hour ticked — the starting point for a newly checked hourly item. */
  const allHoursSelection = () => Object.fromEntries(
    bookedHoursByDate.map(({ dateStr, timeSlots }) => [dateStr, [...timeSlots]])
  );

  const hoursFor = (item) => rentalHours[item.id] || {};
  const hourCountFor = (item) => Object.values(hoursFor(item)).reduce((n, slots) => n + (slots?.length || 0), 0);
  // Session items ignore hours entirely; hourly ones bill per picked hour.
  const lineTotalFor = (item) => getEquipmentLineTotal(item, quantities[item.id] || 0, hourCountFor(item));

  const toggleItem = (item) => {
    if (remainingFor(item) <= 0) return;
    const checking = !quantities[item.id];
    setQuantities(prev => {
      const next = { ...prev };
      if (next[item.id]) {
        delete next[item.id];
      } else {
        next[item.id] = 1;
      }
      return next;
    });
    if (!isHourlyEquipment(item)) return;
    // Checking an hourly item starts with the whole session ticked — the
    // common case is "I want it the whole time", and an item that opened at
    // ₱0 with nothing ticked reads as broken pricing. Unchecking clears the
    // hours so re-checking doesn't restore a stale, half-unticked set.
    setRentalHours(prev => {
      const next = { ...prev };
      if (checking) next[item.id] = allHoursSelection();
      else delete next[item.id];
      return next;
    });
  };

  /**
   * Ticks/unticks one (date, hour) for an hourly item. The last hour cannot
   * be unticked — an hourly rental with no hours is a ₱0 line the club would
   * still have to hand equipment out for, so removing it means unchecking the
   * item itself.
   */
  const toggleRentalHour = (item, dateStr, timeSlot) => {
    setRentalHours(prev => {
      const forItem = prev[item.id] || {};
      const current = forItem[dateStr] || [];
      const has = current.includes(timeSlot);
      if (has && hourCountFor(item) <= 1) return prev;
      const nextSlots = has ? current.filter(s => s !== timeSlot) : [...current, timeSlot];
      const nextForItem = { ...forItem };
      if (nextSlots.length === 0) delete nextForItem[dateStr];
      else nextForItem[dateStr] = sortTimeSlots(nextSlots);
      return { ...prev, [item.id]: nextForItem };
    });
  };

  const setQty = (item, qty) => {
    setQuantities(prev => ({ ...prev, [item.id]: qty }));
  };

  const toggleQtyMenu = (item, maxQty, e) => {
    if (qtyMenu?.itemId === item.id) { setQtyMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setQtyMenu({ itemId: item.id, top: rect.bottom + 6, left: rect.left, width: rect.width, maxQty, unit: item.unit });
  };

  const selectQty = (item, n) => {
    setQty(item, n);
    setQtyMenu(null);
  };

  const selectedItems = equipmentList.filter(item => quantities[item.id] > 0);
  const grandTotal = selectedItems.reduce((sum, item) => sum + lineTotalFor(item), 0);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleConfirm = () => {
    const items = selectedItems.map(item => {
      const price = priceFor(item);
      const qty = quantities[item.id];
      const hourly = isHourlyEquipment(item);
      return {
        id: item.id,
        label: item.label,
        unit: item.unit,
        price,
        qty,
        // Recorded per line rather than looked up from config later: an admin
        // switching an item from hourly to session afterwards must not change
        // what an already-placed booking says it was charged for.
        rentalType: hourly ? 'hourly' : 'session',
        // Hourly only — { [dateStr]: timeSlot[] } and its count, which is what
        // the subtotal was multiplied by and which hours to hold the units for.
        ...(hourly ? { hours: hoursFor(item), hourCount: hourCountFor(item) } : {}),
        subtotal: lineTotalFor(item),
      };
    });
    onConfirm({ items, total: grandTotal });
  };

  // Portalled straight to document.body instead of rendering in place.
  // UserInfoForm sits inside App.jsx's .screen-modal-overlay, which is
  // itself a fixed, overflow-y:auto container — nesting another
  // position:fixed overlay inside that combination is unreliable across
  // browsers (the inner "fixed" element can end up sized/positioned
  // against the scrollable ancestor instead of the real viewport, leaving
  // a gap where the form's own Continue button peeks through uncovered).
  // A portal sidesteps that entirely by attaching this overlay as a direct
  // child of <body>, exactly like the top-level Modal component.
  return createPortal(
    <div className="erm-overlay" onClick={handleBackdropClick}>
      <div className="erm-card" ref={cardRef}>
        <div className="erm-head">
          <h2 className="erm-title">Need equipments?</h2>
          <button type="button" className="erm-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <p className="erm-subtitle">Please select the following equipments you would like to rent</p>

        <div className="erm-list">
          {equipmentList.map(item => {
            const checked = !!quantities[item.id];
            const remaining = remainingFor(item);
            const soldOut = !loadingAvailability && remaining <= 0;
            const qty = quantities[item.id] || 1;
            // Cap the dropdown at what's actually free, not an arbitrary
            // number — otherwise an admin-configured total above 10 (or a
            // wide-open time window) would silently get truncated.
            const maxQty = Math.max(1, remaining || 1);
            return (
              <div className={`erm-item${checked ? ' checked' : ''}${soldOut ? ' erm-soldout' : ''}`} key={item.id}>
                <label className="erm-item-row">
                  <input type="checkbox" checked={checked} disabled={soldOut} onChange={() => toggleItem(item)} />
                  <span className="erm-item-label">{item.label}</span>
                  {/* "per hour" / "per session" spelled out: without it, two
                      items at ₱150/machine bill very differently and the guest
                      can only find out from the total. */}
                  <span className="erm-item-price">{describeEquipmentPrice(item, config)}</span>
                </label>

                {!loadingAvailability && (
                  <p className="erm-availability-note">
                    {soldOut
                      ? `Fully booked for this time slot`
                      : `${remaining} ${remaining === 1 ? item.unit : `${item.unit}s`} available for your selected time`}
                  </p>
                )}

                {checked && (
                  <div className="erm-qty-block">
                    <label className="erm-qty-label" id={`erm-qty-label-${item.id}`}>
                      Number of {item.label.toLowerCase()}
                    </label>
                    <button
                      type="button"
                      className="erm-qty-select"
                      aria-haspopup="listbox"
                      aria-labelledby={`erm-qty-label-${item.id}`}
                      aria-expanded={qtyMenu?.itemId === item.id}
                      onClick={(e) => toggleQtyMenu(item, maxQty, e)}
                    >
                      <span>{qty} {qty === 1 ? item.unit : `${item.unit}s`}</span>
                      <span className="erm-qty-caret" aria-hidden="true">▾</span>
                    </button>
                    {/* Hourly items only: the guest's own booked hours, ticked
                        by default, so they can drop the hours they don't
                        actually want the item for and stop paying for them.
                        Grouped by date because a multi-date booking can hold
                        different hours on each of its dates. */}
                    {isHourlyEquipment(item) && (
                      <div className="erm-hours-block">
                        <span className="erm-hours-label">Rent for these hours</span>
                        {bookedHoursByDate.map(({ dateStr, timeSlots }) => (
                          <div className="erm-hours-date" key={dateStr}>
                            {bookedHoursByDate.length > 1 && (
                              <span className="erm-hours-date-label">{formatPickerDate(dateStr)}</span>
                            )}
                            <div className="erm-hours-chips">
                              {timeSlots.map(slot => {
                                const on = (hoursFor(item)[dateStr] || []).includes(slot);
                                // The final ticked hour locks: an hourly line
                                // with zero hours is a ₱0 rental the club would
                                // still have to hand equipment out for. Dropping
                                // the item entirely is the checkbox above.
                                const isLastHour = on && hourCountFor(item) <= 1;
                                return (
                                  <button
                                    type="button"
                                    key={`${dateStr}-${slot}`}
                                    className={`erm-hour-chip${on ? ' on' : ''}`}
                                    aria-pressed={on}
                                    disabled={isLastHour}
                                    title={isLastHour ? 'Untick the item itself to remove it' : undefined}
                                    onClick={() => toggleRentalHour(item, dateStr, slot)}
                                  >
                                    {formatTimeSlotRange(slot)}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="erm-subtotal-row">
                      <span>
                        {item.label} rental total
                        {isHourlyEquipment(item) && (
                          <span className="erm-subtotal-basis">
                            {' '}&middot; {hourCountFor(item)} hr{hourCountFor(item) === 1 ? '' : 's'}
                          </span>
                        )}
                      </span>
                      <span className="erm-subtotal-amt">{formatMoney(lineTotalFor(item), config)}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="erm-total-row">
          <span>Equipment rental total</span>
          <span className="erm-total-amt">{formatMoney(grandTotal, config)}</span>
        </div>

        <button type="button" className="erm-continue" onClick={handleConfirm}>
          {selectedItems.length > 0 ? 'Continue' : 'Continue without equipment'}
        </button>
      </div>

      {/* Quantity popup — position:fixed with a JS-computed rect so it's
          anchored to its trigger button but, unlike an absolutely-positioned
          popup, is never clipped by erm-card's own overflow-y:auto. Its
          own overflow-y:auto is what actually makes a long option list
          (e.g. an admin-configured inventory of 20) scrollable. */}
      {qtyMenu && (() => {
        const item = equipmentList.find(i => i.id === qtyMenu.itemId);
        if (!item) return null;
        const qty = quantities[item.id] || 1;
        return (
          <ul
            className="erm-qty-menu"
            role="listbox"
            aria-labelledby={`erm-qty-label-${item.id}`}
            style={{ top: qtyMenu.top, left: qtyMenu.left, width: qtyMenu.width }}
            onClick={(e) => e.stopPropagation()}
          >
            {Array.from({ length: qtyMenu.maxQty }, (_, i) => i + 1).map(n => (
              <li
                key={n}
                role="option"
                aria-selected={n === qty}
                className={`erm-qty-option${n === qty ? ' selected' : ''}`}
                onClick={() => selectQty(item, n)}
              >
                {n} {n === 1 ? qtyMenu.unit : `${qtyMenu.unit}s`}
              </li>
            ))}
          </ul>
        );
      })()}
    </div>,
    document.body
  );
}

export default EquipmentRentalModal;
